import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { loadEntryPoint } from 'react-relay';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import { Popup, type PopupProps, type TriggerProps } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import type {
	AnyEntryPoint,
	EntryPointRef,
	ParamsOfEntryPoint,
	RuntimePropsOfEntryPoint,
} from '@atlassian/entry-point-types';
import {
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { useEntrypointErrorAnalytics } from '../../../analytics/use-entrypoint-error-analytics';
import { useOnCloseDialog } from '../../../analytics/use-on-close-dialog';
import { EditionAwarenessButton } from '../../edition-awareness-button';
import type { DialogConfig } from '../shared/types';

const styles = cssMap({
	fallbackBox: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;
const emptyEntryPointProps = {} as RuntimePropsOfEntryPoint<AnyEntryPoint>;

type UnionOmit<T, K extends string | number | symbol> = T extends unknown ? Omit<T, K> : never;

type CustomPopupProps = UnionOmit<PopupProps, 'isOpen' | 'content' | 'trigger'>;

export type InnerProps<T> = {
	entryPoint: T;
	entryPointParams?: ParamsOfEntryPoint<T>;
	entryPointProps?: RuntimePropsOfEntryPoint<T>;
	errorFallback?: ErrorFallback;
} & CustomPopupProps;
type Props<T> = InnerProps<T> & {
	dialogConfig: DialogConfig;
};

export function AutoOpenPopupTrigger<TEntryPoint extends AnyEntryPoint>(
	props: Props<TEntryPoint>,
): React.JSX.Element {
	const { dialogConfig, ...rest } = props;
	return (
		<AnalyticsContext data={dialogConfig}>
			<AutoOpenPopupTriggerInner<TEntryPoint> {...rest} />
		</AnalyticsContext>
	);
}

/**
 * THIS IS A TEMPORARY IMPLEMENTATION, WILL BE REMOVED IN THE FUTURE
 * NOT RECOMMENDED FOR USE ANYWHERE ELSE
 * Supports the auto-open functionality for the Confluence Premium Popup
 */
function AutoOpenPopupTriggerInner<TEntryPoint extends AnyEntryPoint>({
	entryPoint,
	entryPointParams,
	entryPointProps,
	errorFallback,
	onClose,
	...popupProps
}: InnerProps<TEntryPoint>) {
	const environmentProvider = useRelayEnvironmentProvider();
	const onError = useEntrypointErrorAnalytics();
	const onDismiss = useOnCloseDialog();

	const id = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<any>).getModuleName() || 'unknown';
	}, [entryPoint]);

	const [isOpen, setIsOpen] = useState(false);
	const [content, setContent] = useState<ReactNode | null>(null);

	const handleOpen = useCallback(() => {
		setIsOpen(true);
	}, []);

	const handleClose = useCallback(
		(
			event: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | null,
			currentLevel?: any,
		) => {
			setIsOpen(false);
			onClose?.(event, currentLevel);
			onDismiss();
		},
		[onClose, onDismiss],
	);

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const onLoad = useCallback(
		({ reference: entryPointReference }: { reference: EntryPointRef<TEntryPoint> }) => {
			// avoid re-loading entry-point when closing popup
			if (isOpen) {
				return;
			}

			handleOpen();

			setContent(
				<InternalEntryPointContainer
					id={id}
					entryPointReference={entryPointReference}
					fallback={<PopupFallback />}
					errorFallback={errorFallback}
					runtimeProps={entryPointProps || emptyEntryPointProps}
					onError={onError}
				/>,
			);
		},
		[setContent, id, isOpen, entryPointProps, errorFallback, handleOpen, onError],
	);

	const handleToggleAndLoad = useCallback(() => {
		if (isOpen) {
			handleClose(null);
			return;
		}

		handleOpen();
		onLoad({ reference: load() });
	}, [isOpen, handleOpen, onLoad, load, handleClose]);

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps) => {
			return (
				<EditionAwarenessButton
					{...triggerProps}
					ref={triggerProps.ref}
					onClick={handleToggleAndLoad}
				/>
			);
		},
		[handleToggleAndLoad],
	);

	const popupContent = useCallback(() => {
		return content;
	}, [content]);

	const hasRun = useRef(false);

	// Auto-open on load
	useEffect(() => {
		if (!hasRun.current) {
			handleToggleAndLoad?.();
			hasRun.current = true;
		}
	}, [handleToggleAndLoad]);

	return (
		<Popup
			{...popupProps}
			isOpen={isOpen}
			onClose={handleClose}
			trigger={popupTrigger}
			content={popupContent}
			shouldFitContainer={false}
		/>
	);
}

const PopupFallback = () => (
	<Box xcss={styles.fallbackBox}>
		<Spinner />
	</Box>
);
